import { Component, EventEmitter, Input, Output } from "@angular/core";
import { UserNotificationCenter } from "@Core/models/codegen/configuration.models";
import { TenantService } from "@Core/services/codegen/tenant.service";
import { TenantContext } from "@Core/contexts/tenant-context";
import { BehaviorSubject } from "rxjs";
import { AuthService } from "@Services/auth.service";
import { NotificationType } from "@Core/enums/codegen/library.enums";
import { TenantPermissions } from "@Core/enums/codegen/tenant-permissions.enum";

@Component({
    standalone: false,
    selector: 'sm-notification-settings',
    templateUrl: './sm-notification-settings.component.html',
    styleUrls: ['./sm-notification-settings.component.scss']
})
export class SmNotificationSettingsComponent {
    @Input() renderMode: 'modal' | 'inline' = 'modal';

    @Output() save: EventEmitter<void> = new EventEmitter();
    @Output() cancel: EventEmitter<void> = new EventEmitter();

    public model: UserNotificationCenterVM;
    private baselineModelSnapshot: string = '';

    public expirationOptions = [
        { value: 7, label: '7 days' },
        { value: 14, label: '14 days' },
        { value: 30, label: '30 days' }
    ];

    public expirationDropdownItems: DropdownItem[] = this.expirationOptions.map(option => ({
        id: option.value.toString(),
        label: option.label
    }));

    public TENANT_PERMISSIONS = TenantPermissions;

    public get hasChanges(): boolean {
        if (!this.model || !this.baselineModelSnapshot) {
            return false;
        }

        return this.serializeModel(this.model) !== this.baselineModelSnapshot;
    }

    constructor(
        public tenantService: TenantService,
        public tenantContext: TenantContext,
        public authService: AuthService
    ) {
        var userNotificationCenters: BehaviorSubject<UserNotificationCenter[]> =
        this.tenantContext.getStore<UserNotificationCenter>(new UserNotificationCenter()).values;

        userNotificationCenters.subscribe(values => {
            var unc = values.find(un => un.IdentityKey == authService.getIdentityKey());
            if (unc) {

                this.model = {
                    Id: unc.Id,
                    IdentityKey: unc.IdentityKey,
                    EnableInAppNotificationPopups: unc.EnableInAppNotificationPopups,
                    ExpirationDurationOfReadNotificationsInDays: unc.ExpirationDurationOfReadNotificationsInDays,
                    CommentNotifications: true,
                    TaskDelegationNotifications: false,
                    ProgramRevisionNotifications: true,
                    JournalEntryNotifications: true,
                    EmailNotifications: true
                };

                if (unc.NotificationTypeSettings) {
                    this.model.CommentNotifications = unc.NotificationTypeSettings[NotificationType.COMMENT] !== false;
                    this.model.TaskDelegationNotifications = unc.NotificationTypeSettings[NotificationType.TASK_DELEGATION] !== false;
                    this.model.ProgramRevisionNotifications = unc.NotificationTypeSettings[NotificationType.PROGRAM_REVISION] !== false;
                    this.model.JournalEntryNotifications = unc.NotificationTypeSettings[NotificationType.JOURNAL_ENTRY] !== false;
                    this.model.EmailNotifications = unc.NotificationTypeSettings[NotificationType.EMAIL] !== false;
                }

                this.baselineModelSnapshot = this.serializeModel(this.model);
            }
        });
    }

    private serializeModel(model: UserNotificationCenterVM): string {
        return JSON.stringify({
            EnableInAppNotificationPopups: model.EnableInAppNotificationPopups,
            JournalEntryNotifications: model.JournalEntryNotifications,
            CommentNotifications: model.CommentNotifications,
            TaskDelegationNotifications: model.TaskDelegationNotifications,
            ProgramRevisionNotifications: model.ProgramRevisionNotifications,
            EmailNotifications: model.EmailNotifications,
            ExpirationDurationOfReadNotificationsInDays: model.ExpirationDurationOfReadNotificationsInDays
        });
    }

    onToggleChange(event: Event, property: keyof UserNotificationCenterToggleFields): void {
        const target = event.target as { checked?: boolean } | null;
        if (!target || this.model == null || target.checked == null) {
            return;
        }

        this.model[property] = target.checked;
    }

    onExpirationDurationChange(event: CustomEvent<{ value: string }>): void {
        const value = event?.detail?.value;
        if (!value || this.model == null) {
            return;
        }

        const parsedValue = parseInt(value, 10);
        if (!Number.isNaN(parsedValue)) {
            this.model.ExpirationDurationOfReadNotificationsInDays = parsedValue;
        }
    }

    toggleInAppNotificationPopups() {
        this.model.EnableInAppNotificationPopups = !this.model.EnableInAppNotificationPopups;
    }

    toggleJournalEntryNotifications() {
        this.model.JournalEntryNotifications = !this.model.JournalEntryNotifications;
    }

    toggleCommentNotifications() {
        this.model.CommentNotifications = !this.model.CommentNotifications;
    }

    toggleTaskDelegationNotifications() {
        this.model.TaskDelegationNotifications = !this.model.TaskDelegationNotifications;
    }

    toggleProgramRevisionNotifications() {
        this.model.ProgramRevisionNotifications = !this.model.ProgramRevisionNotifications;
    }

    toggleEmailNotifications() {
        this.model.EmailNotifications = !this.model.EmailNotifications;
    }

    onSave() {
        if (!this.model || !this.hasChanges) {
            return;
        }

        var userNotificationCenterToSave = new UserNotificationCenter();
        userNotificationCenterToSave.IdentityKey = this.model.IdentityKey;
        userNotificationCenterToSave.Id = this.model.Id;
        userNotificationCenterToSave.EnableInAppNotificationPopups = this.model.EnableInAppNotificationPopups;
        userNotificationCenterToSave.ExpirationDurationOfReadNotificationsInDays =
            parseInt(this.model.ExpirationDurationOfReadNotificationsInDays.toString());
        userNotificationCenterToSave.NotificationTypeSettings = {
            "ProgramRevision" : this.model.ProgramRevisionNotifications,
            "Comment" : this.model.CommentNotifications,
            "TaskDelegation" : this.model.TaskDelegationNotifications,
            "JournalEntry" : this.model.JournalEntryNotifications,
            "Email": this.model.EmailNotifications
        }

        this.tenantService.UpdateUserNotificationSettings(userNotificationCenterToSave.serialize(), this.tenantContext);
        this.baselineModelSnapshot = this.serializeModel(this.model);

        if (this.renderMode === 'modal') {
            this.save.emit();
        }
    }
}

export interface UserNotificationCenterVM {
    Id: string;
    IdentityKey: string;
    EnableInAppNotificationPopups: boolean;
    JournalEntryNotifications: boolean;
    CommentNotifications: boolean;
    TaskDelegationNotifications: boolean;
    ProgramRevisionNotifications: boolean;
    EmailNotifications: boolean;
    ExpirationDurationOfReadNotificationsInDays: number;
}

type UserNotificationCenterToggleFields = Pick<
    UserNotificationCenterVM,
    'EnableInAppNotificationPopups'
    | 'JournalEntryNotifications'
    | 'CommentNotifications'
    | 'TaskDelegationNotifications'
    | 'ProgramRevisionNotifications'
    | 'EmailNotifications'
>;

type DropdownItem = {
    id: string;
    label: string;
};
